"use client";

import { clickPositionToPercent, isHeatmapCollectionAllowed, normalizeHeatmapEntryOverride, shapeHeatmapClickBatch, shouldSampleHeatmapClick } from "@multi-tenant-cms/seo-runtime";
import type { CookieConsentCategorySelection, CookieConsentConfig, HeatmapClickPoint } from "@multi-tenant-cms/types";
import { useEffect, useRef } from "react";
import { HEATMAP_BOOTSTRAP_EVENT, HEATMAP_PAGE_CONTEXT_KEY, type HeatmapPageWindowContext, isHeatmapConsentAllowed, type TenantHeatmapConfig } from "@/lib/heatmap";
type HeatmapWindow = Window & {
  [HEATMAP_PAGE_CONTEXT_KEY]?: HeatmapPageWindowContext;
};
type Props = {
  config: TenantHeatmapConfig;
  consentConfig: CookieConsentConfig;
  selection: CookieConsentCategorySelection;
};
const MAX_QUEUED_CLICKS = 80;
const TRACKER_VERSION = "heatmap-region-1";
function readPageContext(): HeatmapPageWindowContext {
  if (typeof window === "undefined") {
    return {
      entry_id: null,
      override: "inherit"
    };
  }
  const current = (window as HeatmapWindow)[HEATMAP_PAGE_CONTEXT_KEY];
  if (!current) {
    return {
      entry_id: null,
      override: "inherit"
    };
  }
  return {
    entry_id: current.entry_id,
    override: normalizeHeatmapEntryOverride(current.override)
  };
}
function currentPath(): string {
  const path = window.location.pathname.trim();
  return path.length > 0 ? path : "/";
}
export function HeatmapBootstrap({
  config,
  consentConfig,
  selection
}: Props) {
  const allowed = isHeatmapConsentAllowed(config, consentConfig, selection);
  const allowedRef = useRef(allowed);
  const configRef = useRef(config);
  const queueRef = useRef<HeatmapClickPoint[]>([]);
  const flushInFlightRef = useRef(false);
  const flushTimerRef = useRef<number | null>(null);
  const flushQueueRef = useRef<(options?: {
    keepalive?: boolean;
  }) => Promise<void>>(async () => undefined);
  const scheduleFlushRef = useRef<() => void>(() => undefined);
  allowedRef.current = allowed;
  configRef.current = config;
  scheduleFlushRef.current = () => {
    if (!allowedRef.current || queueRef.current.length === 0 || flushTimerRef.current != null) {
      return;
    }
    const delayMs = Math.min(configRef.current.flush_interval_ms, 2500);
    flushTimerRef.current = window.setTimeout(() => {
      flushTimerRef.current = null;
      flushQueueRef.current().catch(() => undefined);
    }, delayMs);
  };
  flushQueueRef.current = async options => {
    if (!allowedRef.current || flushInFlightRef.current || queueRef.current.length === 0) {
      return;
    }
    if (flushTimerRef.current != null) {
      window.clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    const sendCount = Math.min(queueRef.current.length, configRef.current.batch_size, configRef.current.max_clicks_per_request);
    const pending = queueRef.current.slice(0, sendCount);
    const batch = shapeHeatmapClickBatch({
      clicks: pending,
      config: configRef.current,
      tracker_version: TRACKER_VERSION
    });
    if (batch.clicks.length === 0) {
      queueRef.current = queueRef.current.slice(sendCount);
      return;
    }
    flushInFlightRef.current = true;
    try {
      const response = await fetch(configRef.current.endpoint_path, {
        body: JSON.stringify(batch),
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json"
        },
        keepalive: options?.keepalive ?? false,
        method: "POST"
      });
      if (response.ok) {
        let dropped = false;
        try {
          const body = (await response.json()) as {
            dropped?: unknown;
          };
          dropped = body.dropped === true;
        } catch {
          // Non-JSON success — treat as stored.
        }
        if (dropped) {
          window.setTimeout(() => {
            flushQueueRef.current().catch(() => undefined);
          }, 400);
          return;
        }
        queueRef.current = queueRef.current.slice(sendCount);
        if (queueRef.current.length > 0) {
          scheduleFlushRef.current();
        }
        return;
      }
    } catch {
      // Keep the queue and retry on the next flush.
    } finally {
      flushInFlightRef.current = false;
    }
    scheduleFlushRef.current();
  };
  useEffect(() => {
    window.dispatchEvent(new CustomEvent(HEATMAP_BOOTSTRAP_EVENT, {
      detail: {
        allowed,
        enabled: config.enabled
      }
    }));
    if (!allowed) {
      if (flushTimerRef.current != null) {
        window.clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      queueRef.current = [];
    }
  }, [allowed, config.enabled]);
  useEffect(() => {
    if (!allowed) {
      return;
    }
    const handleClick = (event: MouseEvent) => {
      if (!allowedRef.current) {
        return;
      }
      if (!shouldSampleHeatmapClick(configRef.current.sample_rate, Math.random())) {
        return;
      }
      const root = document.documentElement;
      const point = clickPositionToPercent({
        clientX: event.clientX,
        clientY: event.clientY,
        scrollHeight: Math.max(root.scrollHeight, document.body?.scrollHeight ?? 0, window.innerHeight),
        scrollWidth: Math.max(root.scrollWidth, document.body?.scrollWidth ?? 0, window.innerWidth),
        scrollX: window.scrollX,
        scrollY: window.scrollY
      });
      if (!point) {
        return;
      }
      const pageContext = readPageContext();
      if (!isHeatmapCollectionAllowed({
        config: configRef.current,
        entry_override: pageContext.override
      })) {
        return;
      }
      queueRef.current.push({
        entry_id: pageContext.entry_id,
        occurred_at: new Date().toISOString(),
        path: currentPath(),
        viewport_h: window.innerHeight > 0 ? Math.trunc(window.innerHeight) : 1,
        viewport_w: window.innerWidth > 0 ? Math.trunc(window.innerWidth) : 1,
        x_pct: point.x_pct,
        y_pct: point.y_pct
      });
      if (queueRef.current.length > MAX_QUEUED_CLICKS) {
        queueRef.current = queueRef.current.slice(-MAX_QUEUED_CLICKS);
      }
      if (queueRef.current.length >= configRef.current.batch_size) {
        flushQueueRef.current().catch(() => undefined);
        return;
      }
      scheduleFlushRef.current();
    };
    document.addEventListener("click", handleClick, {
      capture: true
    });
    return () => {
      document.removeEventListener("click", handleClick, {
        capture: true
      });
    };
  }, [allowed]);
  useEffect(() => {
    if (!allowed) {
      return;
    }
    const handlePageHide = () => {
      flushQueueRef.current({
        keepalive: true
      }).catch(() => undefined);
    };
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [allowed]);
  return null;
}